<script setup>
/**
 * RM-360 — the top of the member's request preview link
 * (/referral/preview/<code>): says plainly that this is what goes out, lets
 * the member switch between the request's restaurants, and between what each
 * one gets — its response page (rendered below, by the page itself) and the
 * email, rendered from the same template and context as the real send
 * (GET /reservation/preview-messages). The automated text a restaurant team may
 * also get is not shown to members (Kapil 9/30).
 */
import { computed, ref, watch } from 'vue'
import axios from '@axios'

const props = defineProps({
  previewCode: { type: String, required: true },
  // The request's restaurants, preferred first: [{ _id, name }]
  restaurants: { type: Array, default: () => [] },
  // The restaurant whose page is on screen
  restaurantId: { type: String, default: null },
  submitted: { type: Boolean, default: false },
  // 'page' | 'email'
  tab: { type: String, default: 'page' },
  // RM-400 (Kapil, 10/13): a group/private dining inquiry has no response page — each
  // restaurant's events team gets the email, with the member cc'd. Only the email is shown.
  inquiry: { type: Boolean, default: false },
})

const emit = defineEmits(['update:tab', 'select-restaurant'])

// Opened inside the app (a full-screen dialog with its own close button).
const embedded = typeof window !== 'undefined' && window.self !== window.top

const current = computed(() => props.restaurants.find(r => r._id === props.restaurantId) || props.restaurants[0] || null)
const name = computed(() => current.value?.name || 'the restaurant')

const messages = ref(null)
const loading = ref(false)
const failed = ref(false)

async function load() {
  loading.value = true
  failed.value = false
  try {
    const { data } = await axios.get('/reservation/preview-messages', {
      params: { previewCode: props.previewCode, restaurantId: current.value?._id || undefined },
    })

    messages.value = data
  } catch (e) {
    messages.value = null
    failed.value = true
  } finally {
    loading.value = false
  }
}

watch(() => [props.previewCode, current.value?._id], load, { immediate: true })

const tabs = computed(() => (props.inquiry
  ? [{ value: 'email', label: 'Email' }]
  : [{ value: 'page', label: 'Response page' }, { value: 'email', label: 'Email' }]))

const lead = computed(() => {
  if (props.inquiry) return props.submitted ? `This is the email ${name.value}'s events team received (you were cc'd)` : `This is the email ${name.value}'s events team will receive when you submit (you'll be cc'd)`

  return props.submitted ? `This is what ${name.value} received` : `This is what ${name.value} will receive when you submit`
})

// The email renders in a sandboxed frame: it is the email's own HTML, member
// text included, so scripts never run; links open in a new tab.
const frame = ref(null)
const frameHeight = ref(480)

const srcdoc = computed(() => {
  const html = messages.value?.email?.html
  if (!html) return ''

  return '<!doctype html><html><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank">'
    + '<style>body{margin:0;padding:16px;overflow-wrap:anywhere;}</style>'
    + `</head><body>${html}</body></html>`
})

function fitFrame() {
  const doc = frame.value?.contentDocument
  if (doc?.documentElement) frameHeight.value = doc.documentElement.scrollHeight + 2
}
</script>

<template>
  <section class="request-preview">
    <p
      class="request-preview__lead"
      :class="{ 'request-preview__lead--embedded': embedded }"
    >
      {{ lead }}
    </p>

    <div
      v-if="restaurants.length > 1"
      class="request-preview__restaurants"
    >
      <VChip
        v-for="restaurant in restaurants"
        :key="restaurant._id || restaurant.name"
        :variant="restaurant._id === current?._id ? 'flat' : 'outlined'"
        :color="restaurant._id === current?._id ? 'primary-dark' : undefined"
        size="small"
        @click="emit('select-restaurant', restaurant._id)"
      >
        {{ restaurant.name }}
      </VChip>
    </div>

    <VTabs
      :model-value="tab"
      density="compact"
      color="primary-dark"
      grow
      class="request-preview__tabs"
      @update:model-value="value => emit('update:tab', value)"
    >
      <VTab
        v-for="item in tabs"
        :key="item.value"
        :value="item.value"
      >
        {{ item.label }}
      </VTab>
    </VTabs>

    <div
      v-if="tab !== 'page'"
      class="request-preview__panel"
    >
      <VSkeletonLoader
        v-if="loading"
        type="text, paragraph, paragraph"
      />
      <p
        v-else-if="failed"
        class="request-preview__empty"
      >
        Couldn't load this
        <VBtn
          variant="text"
          size="small"
          color="primary-dark"
          @click="load"
        >
          Try again
        </VBtn>
      </p>

      <template v-else-if="tab === 'email'">
        <template v-if="messages?.email">
          <div class="request-preview__subject">
            <span class="request-preview__label">Subject</span>
            <span class="request-preview__subject-text">{{ messages.email.subject }}</span>
          </div>
          <iframe
            ref="frame"
            class="request-preview__email"
            :srcdoc="srcdoc"
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            :title="`Email to ${name}`"
            :style="{ height: `${frameHeight}px` }"
            @load="fitFrame"
          />
        </template>
        <p
          v-else
          class="request-preview__empty"
        >
          {{ name }} gets no email for this {{ inquiry ? 'inquiry' : 'request' }}
        </p>
      </template>

    </div>
  </section>
</template>

<style lang="scss" scoped>
.request-preview {
  margin-block-end: var(--ds-space-4);
}

.request-preview__lead {
  margin: 0 0 var(--ds-space-3);
  color: var(--ds-text-strong);
  font-size: var(--ds-text-md);
  font-weight: 600;
  line-height: 1.4;
}

.request-preview__lead--embedded {
  padding-inline-end: var(--ds-space-10);
}

.request-preview__restaurants {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-2);
  margin-block-end: var(--ds-space-3);
}

.request-preview__tabs {
  border-block-end: 1px solid var(--ds-border-subtle);
}

.request-preview__panel {
  padding-block-start: var(--ds-space-4);
}

.request-preview__label {
  display: block;
  margin-block-end: var(--ds-space-1);
  color: var(--ds-text-muted);
  font-size: var(--ds-text-xs);
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.request-preview__subject {
  margin-block-end: var(--ds-space-3);
}

.request-preview__subject-text {
  color: var(--ds-text-primary);
  font-size: var(--ds-text-base);
  font-weight: 600;
  line-height: 1.4;
}

.request-preview__email {
  display: block;
  width: 100%;
  border: 1px solid var(--ds-border-subtle);
  border-radius: var(--ds-radius-sm);
  background: #fff;
}

.request-preview__empty {
  margin: 0;
  color: var(--ds-text-muted);
  font-size: var(--ds-text-base);
}
</style>
